import { z } from "zod";
import { FIELD_REQUIRED } from "../../../i18n/keysTranslations";
import {
  ENTERPRISE_INTAKE_BASE_RADIO_OTHER_PAIRS,
  ENTERPRISE_INTAKE_MODULE_CHECK_OTHER_BY_MODULE,
  ENTERPRISE_INTAKE_MODULE_OPTIONAL_NUMERIC,
  ENTERPRISE_INTAKE_MODULE_PEAK_PAIRS,
  ENTERPRISE_INTAKE_MODULE_RADIO_OTHER_BY_MODULE,
  ENTERPRISE_INTAKE_MODULE_REQUIRED_CHECKBOX,
  ENTERPRISE_INTAKE_MODULE_REQUIRED_NUMERIC,
  ENTERPRISE_INTAKE_MODULE_REQUIRED_RADIO,
} from "../config/enterpriseIntakeModuleFieldRegistry";
import {
  ENTERPRISE_INTAKE_SUPPORT_CHECK_OTHER_PAIRS,
  ENTERPRISE_INTAKE_SUPPORT_RADIO_OTHER_PAIRS,
  ENTERPRISE_INTAKE_SUPPORT_REQUIRED_CHECKBOX,
  ENTERPRISE_INTAKE_SUPPORT_REQUIRED_RADIO,
} from "../config/enterpriseIntakeSupportFieldRegistry";
import {
  isModuleSelected,
  isNonNegativeIntegerString,
  isOptionalNonNegativeIntegerString,
  PEAK_DEMAND_VALUE,
} from "../enterpriseIntakeModuleUtils";

const addRequiredIssue = ({ ctx, path, t }) => {
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: t(FIELD_REQUIRED),
    path: Array.isArray(path) ? path : [path],
  });
};

const validateRadioOtherPairs = ({ data, ctx, t, pairs }) => {
  for (const [field, otherField] of pairs) {
    if (data[field] === "other" && !data[otherField]?.trim()) {
      addRequiredIssue({ ctx, path: otherField, t });
    }
  }
};

const validateCheckOtherPairs = ({ data, ctx, t, pairs }) => {
  for (const [field, otherField] of pairs) {
    if (
      Array.isArray(data[field]) &&
      data[field].includes("other") &&
      !data[otherField]?.trim()
    ) {
      addRequiredIssue({ ctx, path: otherField, t });
    }
  }
};

const validateModuleFields = ({ data, ctx, t }) => {
  const selectedModules = data.selectedAdleModules ?? [];

  if (!selectedModules.length) {
    addRequiredIssue({ ctx, path: "selectedAdleModules", t });
  }

  for (const moduleValue of selectedModules) {
    const requiredNumeric =
      ENTERPRISE_INTAKE_MODULE_REQUIRED_NUMERIC[moduleValue] ?? [];
    for (const field of requiredNumeric) {
      if (!isNonNegativeIntegerString(data[field])) {
        addRequiredIssue({ ctx, path: field, t });
      }
    }

    const optionalNumeric =
      ENTERPRISE_INTAKE_MODULE_OPTIONAL_NUMERIC[moduleValue] ?? [];
    for (const field of optionalNumeric) {
      if (!isOptionalNonNegativeIntegerString(data[field])) {
        addRequiredIssue({ ctx, path: field, t });
      }
    }

    const requiredRadio =
      ENTERPRISE_INTAKE_MODULE_REQUIRED_RADIO[moduleValue] ?? [];
    for (const field of requiredRadio) {
      if (!data[field]?.trim()) {
        addRequiredIssue({ ctx, path: field, t });
      }
    }

    const requiredCheckbox =
      ENTERPRISE_INTAKE_MODULE_REQUIRED_CHECKBOX[moduleValue] ?? [];
    for (const field of requiredCheckbox) {
      if (!Array.isArray(data[field]) || data[field].length === 0) {
        addRequiredIssue({ ctx, path: field, t });
      }
    }

    const radioOtherPairs =
      ENTERPRISE_INTAKE_MODULE_RADIO_OTHER_BY_MODULE[moduleValue] ?? [];
    validateRadioOtherPairs({ data, ctx, t, pairs: radioOtherPairs });

    const checkOtherPairs =
      ENTERPRISE_INTAKE_MODULE_CHECK_OTHER_BY_MODULE[moduleValue] ?? [];
    validateCheckOtherPairs({ data, ctx, t, pairs: checkOtherPairs });
  }

  for (const {
    module,
    variationField,
    peakField,
  } of ENTERPRISE_INTAKE_MODULE_PEAK_PAIRS) {
    if (!isModuleSelected({ selectedModules, moduleValue: module })) continue;
    if (data[variationField] === PEAK_DEMAND_VALUE) {
      if (!isNonNegativeIntegerString(data[peakField])) {
        addRequiredIssue({ ctx, path: peakField, t });
      }
    }
  }
};

const validateSupportFieldsFromRegistry = ({ data, ctx, t }) => {
  for (const field of ENTERPRISE_INTAKE_SUPPORT_REQUIRED_RADIO) {
    if (!data[field]?.trim()) {
      addRequiredIssue({ ctx, path: field, t });
    }
  }

  for (const field of ENTERPRISE_INTAKE_SUPPORT_REQUIRED_CHECKBOX) {
    if (!Array.isArray(data[field]) || data[field].length === 0) {
      addRequiredIssue({ ctx, path: field, t });
    }
  }

  validateRadioOtherPairs({
    data,
    ctx,
    t,
    pairs: ENTERPRISE_INTAKE_SUPPORT_RADIO_OTHER_PAIRS,
  });
  validateCheckOtherPairs({
    data,
    ctx,
    t,
    pairs: ENTERPRISE_INTAKE_SUPPORT_CHECK_OTHER_PAIRS,
  });
};

export const validateModulesFromRegistry = ({ data, ctx, t }) => {
  validateModuleFields({ data, ctx, t });
  validateSupportFieldsFromRegistry({ data, ctx, t });
  validateRadioOtherPairs({
    data,
    ctx,
    t,
    pairs: ENTERPRISE_INTAKE_BASE_RADIO_OTHER_PAIRS,
  });
};
